// SCR-05 insights (plan 10): 8 items × 19 cards, card options (10.3.1), per-card exceptions (10.3.3), saved views
// (10.3.4), URL keys (10.3.5), limits (10.6). Stats read rollups only. Metric ids follow the plan 10.1 dictionary.
import { z } from 'zod';
import { MissingReason, PartialReason, ReasonCategory, ReasonDetail, SavedViewScope, SupportItem, Tool } from '../enums.js';
import { Phase, ViewScope } from '../roles-menus.js';
import {
  AppliedFilter,
  CardFooter,
  Comparison,
  Count,
  EpochMs,
  ExcludedCounts,
  Id,
  MethodFilter,
  ModelFilter,
  PeriodParam,
  PersonRef,
  TeamId,
  ToolFilter,
  ValuedNumber,
  ExportFormat,
  filterQuery,
} from '../common.js';
import { ChartData, SummaryFacts } from '../charts.js';

// ───────────────────────── Items, cards ─────────────────────────

export const STATS_ITEMS = ['prompts', 'usage', 'files', 'changes', 'time', 'models', 'actions', 'api'] as const;
export const StatsItem = z.enum(STATS_ITEMS);
export type StatsItem = z.infer<typeof StatsItem>;
/** Old/alias item names accepted from URLs (mockup) → canonical item. */
export const STATS_ITEM_ALIASES: Record<string, StatsItem> = { questions: 'prompts', question: 'prompts', prompt: 'prompts', tokens: 'usage', cost: 'api', costs: 'api', model: 'models', action: 'actions', errors: 'actions' };

export const CARD_IDS = [
  'prompts-trend',
  'prompts-heat',
  'usage-trend',
  'usage-top',
  'files-ext',
  'files-folders',
  'changes-lines',
  'changes-ext',
  'time-active',
  'time-open',
  'time-wait',
  'models-share',
  'models-trend',
  'actions-kinds',
  'actions-decisions',
  'actions-errors',
  'api-calls',
  'api-reconcile',
  'api-costs',
] as const;
export const CardId = z.enum(CARD_IDS);
export type CardId = z.infer<typeof CardId>;

// ───────────────────────── Metric dictionary (plan 10.1) ─────────────────────────

/** Bumped when a definition changes; stored in saved views and export headers (plan 10.1). */
export const METRIC_DICTIONARY_VERSION = '1';

export const METRIC_IDS = [
  'prompt_count',
  'prompt_length',
  'prompts_per_conversation',
  'slash_command_count',
  'tokens_new',
  'tokens_cache_read',
  'tokens_cache_write',
  'tokens_output',
  'tokens_total',
  'tokens_reasoning',
  'cache_reuse_ratio',
  'cost_cli_estimate',
  'cost_unofficial',
  'cost_price_table',
  'cost_org_billed',
  'files_opened',
  'files_changed',
  'files_by_folder',
  'files_by_ext',
  'lines_added',
  'lines_removed',
  'patch_count',
  'commit_pr_count',
  'active_time',
  'conversation_open_time',
  'wait_p50',
  'wait_p95',
  'first_token',
  'model_share',
  'effort_share',
  'action_count',
  'action_fail_rate',
  'command_count',
  'decisions',
  'errors',
  'gateway_call_count',
  'key_usage',
  'org_report_usage',
  'budget_use',
  'active_users',
] as const;
export const MetricId = z.enum(METRIC_IDS);
export type MetricId = z.infer<typeof MetricId>;

/**
 * Card metric option values: dictionary ids + composites the cards draw as one choice —
 * `lines` (added+removed pair), `wait` (typical+slow pair), `lines_by_ext` (Codex only),
 * `request_count` (models-trend), and for actions-errors: `errors` (AI-call errors & tool failures, by tool or error
 * type), `request_failures` (failed requests & refusals), `tool_rejects` (rejected actions), `retries` (plan FR-66).
 */
export const CARD_METRICS = [...METRIC_IDS, 'lines', 'wait', 'lines_by_ext', 'request_count', 'request_failures', 'tool_rejects', 'retries'] as const;
export const CardMetric = z.enum(CARD_METRICS);
export type CardMetric = z.infer<typeof CardMetric>;

/** Display unit of a stats value (not the stored metric_points unit of BUILD-SPEC-data). */
export const CARD_UNITS = ['count', 'chars', 'tokens', 'usd_micros', 'ms', 'pct', 'files', 'lines', 'people'] as const;
export type CardUnit = (typeof CARD_UNITS)[number];
export interface MetricDef {
  unit: CardUnit;
  /** How values combine across records (ratio = numerator/denominator with the denominator shown). */
  reduce: 'sum' | 'p50' | 'p95' | 'ratio' | 'avg' | 'distinct';
  /** Person comparison and person ranking are forbidden (plan 10.6.2). */
  noPerson: boolean;
  /** Value-order sorting of people possible when the policy toggle is on (usage & cost only). */
  valueSort: boolean;
  /** Hidden under 3 people unless self-only (plan 7.6). */
  smallGroup: boolean;
  /** Never added to a total (reasoning is inside output; cost kinds are not summed with each other). */
  notSummed: boolean;
  phase: Phase;
}
const M = (unit: CardUnit, reduce: MetricDef['reduce'], o: Partial<Omit<MetricDef, 'unit' | 'reduce'>> = {}): MetricDef => ({
  unit,
  reduce,
  noPerson: false,
  valueSort: false,
  smallGroup: false,
  notSummed: false,
  phase: 'P2',
  ...o,
});
export const METRIC_DEFS: Record<MetricId, MetricDef> = {
  prompt_count: M('count', 'sum'),
  prompt_length: M('chars', 'p50', { noPerson: true }),
  prompts_per_conversation: M('count', 'p50', { noPerson: true }),
  slash_command_count: M('count', 'sum'),
  tokens_new: M('tokens', 'sum', { valueSort: true }),
  tokens_cache_read: M('tokens', 'sum', { valueSort: true }),
  tokens_cache_write: M('tokens', 'sum', { valueSort: true }),
  tokens_output: M('tokens', 'sum', { valueSort: true }),
  tokens_total: M('tokens', 'sum', { valueSort: true }),
  tokens_reasoning: M('tokens', 'sum', { notSummed: true }),
  cache_reuse_ratio: M('pct', 'ratio'),
  cost_cli_estimate: M('usd_micros', 'sum', { valueSort: true, notSummed: true }),
  cost_unofficial: M('usd_micros', 'sum', { valueSort: true, notSummed: true }),
  cost_price_table: M('usd_micros', 'sum', { valueSort: true, notSummed: true, phase: 'P4' }),
  cost_org_billed: M('usd_micros', 'sum', { notSummed: true, phase: 'P4' }),
  files_opened: M('files', 'distinct'),
  files_changed: M('files', 'distinct', { noPerson: true }),
  files_by_folder: M('files', 'distinct'),
  files_by_ext: M('files', 'distinct'),
  lines_added: M('lines', 'sum', { noPerson: true }),
  lines_removed: M('lines', 'sum', { noPerson: true }),
  patch_count: M('count', 'sum', { noPerson: true }),
  commit_pr_count: M('count', 'sum', { noPerson: true }),
  active_time: M('ms', 'sum', { noPerson: true, smallGroup: true }),
  conversation_open_time: M('ms', 'p50', { noPerson: true }),
  wait_p50: M('ms', 'p50'),
  wait_p95: M('ms', 'p95'),
  first_token: M('ms', 'p50'),
  model_share: M('pct', 'ratio'),
  effort_share: M('pct', 'ratio'),
  action_count: M('count', 'sum'),
  action_fail_rate: M('pct', 'ratio'),
  command_count: M('count', 'sum'),
  decisions: M('count', 'sum'),
  errors: M('count', 'sum'),
  gateway_call_count: M('count', 'sum', { phase: 'P4' }),
  key_usage: M('tokens', 'sum', { phase: 'P4' }),
  org_report_usage: M('tokens', 'sum', { phase: 'P4' }),
  budget_use: M('pct', 'ratio', { phase: 'P4' }),
  active_users: M('people', 'distinct', { smallGroup: true }),
};

// ───────────────────────── Options (plan 10.3.1) ─────────────────────────

/** Comparison keys (`by`). Card-fixed groupings (ext, folder, action_group, decision, cost_kind) are listed too. */
export const BY_KEYS = [
  'none',
  'method',
  'tool',
  'model',
  'effort',
  'surface',
  'auth_mode',
  'team',
  'team_now',
  'person',
  'token_kind',
  'ext',
  'folder',
  'action_group',
  'decision',
  'error_kind',
  'api_key',
  'cost_kind',
] as const;
export const ByKey = z.enum(BY_KEYS);
export type ByKey = z.infer<typeof ByKey>;
export const AGGS = ['sum', 'per_person', 'avg', 'p50', 'p95'] as const;
export const Agg = z.enum(AGGS);
export type Agg = z.infer<typeof Agg>;
export const CHART_KINDS = ['stacked_bar', 'bar', 'line', 'stacked_area', 'hbar', 'donut', 'heatmap', 'histogram', 'bar100', 'diverging', 'table'] as const;
export const ChartKind = z.enum(CHART_KINDS);
export type ChartKind = z.infer<typeof ChartKind>;
export const UNIT_OPTIONS = ['auto', 'hour', 'day', 'week', 'month'] as const;
export const UnitOption = z.enum(UNIT_OPTIONS);
export const TOP_OPTIONS = [5, 10, 20] as const;
export const DISP_OPTIONS = ['auto', 'raw', 'k', 'man', 'm', 'usd', 'krw'] as const;
export const DispOption = z.enum(DISP_OPTIONS);
export type DispOption = z.infer<typeof DispOption>;
export const SORT_OPTIONS = ['name', 'value'] as const;

/** Option URL keys (`o.<card>.<key>` on the screen URL; plain keys on the API query). */
export const OPTION_KEYS = ['period', 'unit', 'by', 'metric', 'agg', 'chart', 'top', 'prev', 'disp', 'hide0', 'split', 'unoff', 'scope', 'sort'] as const;
export type OptionKey = (typeof OPTION_KEYS)[number];

const Flag = z.enum(['0', '1']);
/** The card option part of the query (all optional — missing = card default). */
export const cardOptionShape = {
  unit: UnitOption.optional(),
  by: ByKey.optional(),
  metric: CardMetric.optional(),
  agg: Agg.optional(),
  chart: ChartKind.optional(),
  top: z.coerce.number().pipe(z.union([z.literal(5), z.literal(10), z.literal(20)])).optional(),
  prev: Flag.optional(),
  disp: DispOption.optional(),
  hide0: Flag.optional(),
  split: Flag.optional(),
  unoff: Flag.optional(),
  sort: z.enum(SORT_OPTIONS).optional(),
};

/**
 * GET /api/v1/stats/cards/:cardId — common filter (with the card's own period/scope if it overrides them: a card may
 * only narrow the scope) + tool/model + card options. Invalid values are 400; not-allowed-but-valid values are reset to
 * the card default and reported in `notes` (option_reset).
 */
export const StatsCardQuery = filterQuery({ tool: ToolFilter.optional(), model: ModelFilter.optional(), ...cardOptionShape });
export type StatsCardQuery = z.infer<typeof StatsCardQuery>;
export const CardParams = z.object({ cardId: CardId });

/** Effective options after defaults/clamps. */
export const StatsCardOptions = z.object({
  unit: z.enum(['hour', 'day', 'week', 'month']).nullable(),
  by: ByKey,
  metric: CardMetric,
  agg: Agg,
  chart: ChartKind,
  top: z.union([z.literal(5), z.literal(10), z.literal(20)]).nullable(),
  prev: z.boolean(),
  disp: DispOption,
  hide0: z.boolean(),
  split: z.boolean(),
  unoff: z.boolean(),
  sort: z.enum(SORT_OPTIONS),
});
export type StatsCardOptions = z.infer<typeof StatsCardOptions>;

/** Fixed sentences the card must show (plan 10.6.3 keys) + data notes. The web words them. */
export const STATS_NOTES = [
  'option_reset',
  'hour_clamped',
  'approx_latency',
  'few_samples',
  'codex_estimated_lines',
  'cli_metrics_off',
  'auth_mode_estimated',
  'auth_mode_cli_only',
  'folder_names_hidden',
  'unofficial_hidden',
  'cost_kinds_not_summed',
  'org_report_daily',
  'key_grouping_unavailable',
  'via_gateway_separate',
  'per_person_denominator',
  'reasoning_not_summed',
  'codex_cache_write_excluded',
  'codex_tokens_verifying',
  'shell_files_missing',
  'not_evaluation',
  'not_work_time',
  'not_task_time',
  'not_contribution',
  'not_bill',
  'allow_not_success',
  'person_name_order',
] as const;
export const StatsNote = z.enum(STATS_NOTES);
export type StatsNote = z.infer<typeof StatsNote>;

/** Empty-state variants (plan 10.4.3). */
export const EMPTY_KINDS = ['not_connected', 'no_match', 'small_group', 'level_off', 'tool_not_sent', 'metrics_off', 'gateway_before', 'api_only', 'cli_only'] as const;
export const EmptyKind = z.enum(EMPTY_KINDS);

export const CardBadge = z.object({ state: z.enum(['none', 'partial']), reason: MissingReason.optional(), partial: PartialReason.optional(), detail: ReasonDetail.optional(), tool: Tool.optional(), item: SupportItem.optional() });

export const StatsCardResponse = z.object({
  cardId: CardId,
  item: StatsItem,
  applied: AppliedFilter,
  options: StatsCardOptions,
  notes: z.array(StatsNote),
  /** Forbidden comparison/sort options never reach here: they are 403 (plan 10.6.4). */
  state: z.enum(['ok', 'empty', 'small_group', 'unavailable']),
  empty: EmptyKind.nullable(),
  chart: ChartData.nullable(),
  summary: SummaryFacts,
  /** Card-level badges ("일부만 · 검증 중" for Codex, "기록 없음 · Claude는 따로 보내지 않음" for reasoning…). */
  badges: z.array(CardBadge),
  /**
   * `reasoningExcluded`: records left out of the reasoning line ("생각 사용량 기록 없음 · n건·n명 제외"), with
   * `excludedReasonsByTool` naming which tool each reason belongs to.
   */
  extras: z.object({
    viaGatewayCalls: Count.optional(),
    excludedReasonsByTool: z.partialRecord(Tool, MissingReason).optional(),
    reasoningExcluded: ExcludedCounts.optional(),
  }),
  footer: CardFooter,
  dictionaryVersion: z.string(),
});
export type StatsCardResponse = z.infer<typeof StatsCardResponse>;

/** GET /api/v1/stats/context — screen-level facts: small-group alert, which items are open (api = P4/gateway). */
export const StatsContextQuery = filterQuery({ tool: ToolFilter.optional(), model: ModelFilter.optional() });
export const StatsContextResponse = z.object({
  applied: AppliedFilter,
  models: z.array(z.object({ id: z.string(), label: z.string() })),
  people: Count.nullable(),
  smallGroup: z.boolean(),
  smallGroupMin: z.number().int().min(1).optional(),
  reason: MissingReason.optional(),
  teamFilterReset: z.literal('small_group').optional(),
  items: z.array(z.object({ item: StatsItem, available: z.boolean(), reason: z.enum(['before_phase', 'gateway_off']).nullable() })),
  /** Person comparison needs people.view; team comparison needs team+ scope. */
  allowedBy: z.object({ person: z.boolean(), team: z.boolean() }),
  allowValueSort: z.boolean(),
  dictionaryVersion: z.string(),
});

/** GET /api/v1/stats/usage-kpis — the usage item's number tiles. */
export const UsageKpisResponse = z.object({
  applied: AppliedFilter,
  teamFilterReset: z.literal('small_group').optional(),
  tokens: Comparison,
  kinds: z.object({ new: ValuedNumber, cacheRead: ValuedNumber, cacheWrite: ValuedNumber, output: ValuedNumber }),
  cacheReuse: z.object({ pct: ValuedNumber, denominator: z.number().nonnegative().nullable() }),
  reasoning: ValuedNumber,
  costs: z.object({ cliEstimate: ValuedNumber, gatewayPrice: ValuedNumber }),
});

// ───────────────────────── Per-card spec (plan 10.2, 10.3.3) ─────────────────────────

export interface CardSpec {
  item: StatsItem;
  phase: Phase;
  metrics: readonly CardMetric[];
  metric: CardMetric;
  /** Comparison choices; null = the card has no `by` option (fixed grouping in `fixedBy`). */
  by: readonly ByKey[] | null;
  byDefault: ByKey;
  fixedBy?: ByKey;
  aggs: readonly Agg[];
  charts: readonly ChartKind[];
  chart: ChartKind;
  top: boolean;
  prev: boolean;
  unit: boolean;
  hide0: boolean;
  split?: boolean;
  unoff?: boolean;
  disp?: boolean;
  /** Card may narrow scope; files cards default to 'me' (plan 10.3.2). */
  scopeDefault?: ViewScope;
  smallGroup: boolean;
  /** Person comparison forbidden for every metric of this card. */
  noPerson: boolean;
  fixedNotes: readonly StatsNote[];
  /**
   * Choices and defaults that change with the chosen metric or comparison (mockup stats.js `allowed`/`metricDefaults`/
   * `byDefaults`). Applied in order on top of the card spec by `resolveCardSpec`.
   */
  variants?: readonly CardVariant[];
}

export interface CardVariant {
  /** Applies when the chosen metric is one of these… */
  metric?: readonly CardMetric[];
  /** …and/or the chosen comparison is one of these. */
  by?: readonly ByKey[];
  set: {
    by?: readonly ByKey[] | null;
    byDefault?: ByKey;
    aggs?: readonly Agg[];
    agg?: Agg;
    charts?: readonly ChartKind[];
    chart?: ChartKind;
    /** Allowed `top` values; [] = no top option (e.g. usage by token kind: always 4). */
    tops?: readonly (5 | 10 | 20)[];
    disps?: readonly DispOption[];
  };
}

/** A card's effective choices for one metric/comparison pair — what the option panel offers and the server accepts. */
export interface ResolvedCardSpec {
  metrics: readonly CardMetric[];
  metric: CardMetric;
  by: readonly ByKey[] | null;
  byValue: ByKey;
  byDefault: ByKey;
  aggs: readonly Agg[];
  agg: Agg;
  charts: readonly ChartKind[];
  chart: ChartKind;
  tops: readonly (5 | 10 | 20)[];
  disps: readonly DispOption[];
}

const TREND_BY = ['method', 'tool', 'model', 'effort', 'surface', 'auth_mode', 'team', 'team_now', 'person'] as const;
export const CARD_SPECS: Record<CardId, CardSpec> = {
  'prompts-trend': {
    item: 'prompts',
    phase: 'P2',
    metrics: ['prompt_count', 'prompts_per_conversation', 'slash_command_count', 'prompt_length'],
    metric: 'prompt_count',
    by: TREND_BY,
    byDefault: 'method',
    aggs: ['sum', 'per_person'],
    charts: ['stacked_bar', 'bar', 'line', 'table'],
    chart: 'stacked_bar',
    variants: [
      { metric: ['prompt_length', 'prompts_per_conversation'], set: { by: TREND_BY.filter((b) => b !== 'person'), aggs: ['p50', 'avg'], agg: 'p50', charts: ['line', 'bar', 'table'], chart: 'line' } },
      { by: ['person'], set: { aggs: ['sum'], agg: 'sum', charts: ['hbar', 'table'], chart: 'hbar' } },
    ],
    top: true,
    prev: true,
    unit: true,
    hide0: true,
    smallGroup: false,
    noPerson: false,
    fixedNotes: [],
  },
  'prompts-heat': {
    item: 'prompts',
    phase: 'P2',
    metrics: ['prompt_count'],
    metric: 'prompt_count',
    by: null,
    byDefault: 'none',
    aggs: ['sum'],
    charts: ['heatmap', 'table'],
    chart: 'heatmap',
    top: false,
    prev: false,
    unit: false,
    hide0: false,
    smallGroup: true,
    noPerson: true,
    fixedNotes: [],
  },
  'usage-trend': {
    item: 'usage',
    phase: 'P2',
    metrics: ['tokens_total', 'cost_cli_estimate', 'cost_unofficial', 'cost_price_table'],
    metric: 'tokens_total',
    by: ['token_kind', 'method', 'tool', 'model', 'auth_mode', 'team'],
    byDefault: 'token_kind',
    aggs: ['sum', 'per_person'],
    charts: ['stacked_area', 'stacked_bar', 'table'],
    chart: 'stacked_area',
    variants: [
      { metric: ['cost_cli_estimate', 'cost_unofficial', 'cost_price_table'], set: { by: ['method', 'tool', 'model', 'auth_mode', 'team'], byDefault: 'method', disps: ['auto'] } },
      // The four token kinds are always four: no top N.
      { by: ['token_kind'], set: { tops: [] } },
    ],
    top: true,
    prev: true,
    unit: true,
    hide0: true,
    disp: true,
    smallGroup: false,
    noPerson: false,
    fixedNotes: ['reasoning_not_summed'],
  },
  'usage-top': {
    item: 'usage',
    phase: 'P2',
    metrics: ['tokens_total', 'cache_reuse_ratio'],
    metric: 'tokens_total',
    by: ['model', 'tool', 'method', 'surface', 'effort', 'auth_mode', 'team', 'team_now', 'person'],
    byDefault: 'model',
    aggs: ['sum'],
    charts: ['hbar', 'table'],
    chart: 'hbar',
    // Person comparison only for usage, never for the reuse ratio.
    variants: [{ metric: ['cache_reuse_ratio'], set: { by: ['model', 'tool', 'method', 'surface', 'effort', 'auth_mode', 'team', 'team_now'] } }],
    top: true,
    prev: false,
    unit: false,
    hide0: false,
    smallGroup: false,
    noPerson: false,
    fixedNotes: [],
  },
  'files-ext': {
    item: 'files',
    phase: 'P2',
    metrics: ['files_by_ext', 'files_opened', 'files_changed'],
    metric: 'files_by_ext',
    by: null,
    byDefault: 'ext',
    fixedBy: 'ext',
    aggs: ['sum'],
    charts: ['donut', 'hbar', 'table'],
    chart: 'donut',
    top: true,
    prev: false,
    unit: false,
    hide0: false,
    split: true,
    scopeDefault: 'me',
    smallGroup: false,
    noPerson: true,
    fixedNotes: ['shell_files_missing'],
  },
  'files-folders': {
    item: 'files',
    phase: 'P2',
    metrics: ['files_by_folder'],
    metric: 'files_by_folder',
    by: null,
    byDefault: 'folder',
    fixedBy: 'folder',
    aggs: ['sum'],
    charts: ['hbar', 'table'],
    chart: 'hbar',
    top: true,
    prev: false,
    unit: false,
    hide0: false,
    scopeDefault: 'me',
    smallGroup: false,
    noPerson: true,
    fixedNotes: [],
  },
  'changes-lines': {
    item: 'changes',
    phase: 'P2',
    metrics: ['lines', 'files_changed', 'patch_count', 'commit_pr_count'],
    metric: 'lines',
    by: ['tool'],
    byDefault: 'tool',
    aggs: ['sum'],
    charts: ['diverging', 'stacked_bar', 'bar', 'line', 'table'],
    chart: 'diverging',
    variants: [
      { metric: ['lines'], set: { charts: ['diverging', 'line', 'table'], chart: 'diverging' } },
      { metric: ['files_changed', 'patch_count'], set: { charts: ['stacked_bar', 'line', 'table'], chart: 'stacked_bar' } },
      { metric: ['commit_pr_count'], set: { charts: ['bar', 'line', 'table'], chart: 'bar' } },
    ],
    top: false,
    prev: true,
    unit: true,
    hide0: true,
    smallGroup: false,
    noPerson: true,
    fixedNotes: ['not_contribution'],
  },
  'changes-ext': {
    item: 'changes',
    phase: 'P2',
    metrics: ['files_changed', 'lines_by_ext'],
    metric: 'files_changed',
    by: null,
    byDefault: 'ext',
    fixedBy: 'ext',
    aggs: ['sum'],
    charts: ['hbar', 'table'],
    chart: 'hbar',
    top: true,
    prev: false,
    unit: false,
    hide0: false,
    smallGroup: false,
    noPerson: true,
    fixedNotes: ['not_contribution'],
  },
  'time-active': {
    item: 'time',
    phase: 'P2',
    metrics: ['active_time'],
    metric: 'active_time',
    by: ['none', 'surface', 'model', 'team', 'team_now'],
    byDefault: 'none',
    aggs: ['sum', 'per_person'],
    charts: ['bar', 'line', 'table'],
    chart: 'bar',
    top: false,
    prev: true,
    unit: true,
    hide0: true,
    smallGroup: true,
    noPerson: true,
    fixedNotes: ['not_work_time'],
  },
  'time-open': {
    item: 'time',
    phase: 'P2',
    metrics: ['conversation_open_time'],
    metric: 'conversation_open_time',
    by: ['none', 'tool'],
    byDefault: 'none',
    aggs: ['p50'],
    charts: ['histogram', 'table'],
    chart: 'histogram',
    top: false,
    prev: false,
    unit: false,
    hide0: false,
    smallGroup: false,
    noPerson: true,
    fixedNotes: ['not_task_time'],
  },
  'time-wait': {
    item: 'time',
    phase: 'P2',
    metrics: ['wait', 'first_token'],
    metric: 'wait',
    by: ['none', 'tool', 'model'],
    byDefault: 'none',
    aggs: ['p50', 'p95'],
    charts: ['line', 'bar', 'table'],
    chart: 'line',
    // "보통/느릴 때" both lines without a comparison; p95 is a choice only for wait with a comparison.
    variants: [
      { metric: ['first_token'], set: { aggs: ['p50'], agg: 'p50' } },
      { by: ['none'], set: { aggs: ['p50'], agg: 'p50' } },
    ],
    top: false,
    prev: true,
    unit: true,
    hide0: false,
    smallGroup: false,
    noPerson: true,
    fixedNotes: [],
  },
  'models-share': {
    item: 'models',
    phase: 'P2',
    metrics: ['tokens_total', 'prompt_count', 'cost_cli_estimate', 'cost_unofficial', 'cost_price_table'],
    metric: 'tokens_total',
    by: null,
    byDefault: 'model',
    fixedBy: 'model',
    aggs: ['sum'],
    charts: ['donut', 'table'],
    chart: 'donut',
    top: false,
    prev: false,
    unit: false,
    hide0: false,
    smallGroup: false,
    noPerson: true,
    fixedNotes: [],
  },
  'models-trend': {
    item: 'models',
    phase: 'P2',
    metrics: ['request_count', 'tokens_total'],
    metric: 'request_count',
    by: ['effort', 'model'],
    byDefault: 'effort',
    aggs: ['sum'],
    charts: ['stacked_bar', 'table'],
    chart: 'stacked_bar',
    top: true,
    prev: true,
    unit: true,
    hide0: true,
    smallGroup: false,
    noPerson: true,
    fixedNotes: [],
  },
  'actions-kinds': {
    item: 'actions',
    phase: 'P2',
    metrics: ['action_count', 'action_fail_rate', 'command_count'],
    metric: 'action_count',
    by: null,
    byDefault: 'action_group',
    fixedBy: 'action_group',
    aggs: ['sum'],
    charts: ['hbar', 'table'],
    chart: 'hbar',
    // Commands are counted per tool, not per action group.
    variants: [{ metric: ['command_count'], set: { byDefault: 'tool' } }],
    top: true,
    prev: false,
    unit: false,
    hide0: false,
    split: true,
    smallGroup: false,
    noPerson: true,
    fixedNotes: [],
  },
  'actions-decisions': {
    item: 'actions',
    phase: 'P2',
    metrics: ['decisions'],
    metric: 'decisions',
    by: null,
    byDefault: 'decision',
    fixedBy: 'decision',
    aggs: ['sum'],
    charts: ['bar100', 'table'],
    chart: 'bar100',
    top: false,
    prev: false,
    unit: false,
    hide0: false,
    smallGroup: false,
    noPerson: true,
    fixedNotes: ['allow_not_success'],
  },
  'actions-errors': {
    item: 'actions',
    phase: 'P2',
    metrics: ['errors', 'request_failures', 'tool_rejects', 'retries'],
    metric: 'errors',
    by: ['tool', 'error_kind', 'method'],
    byDefault: 'tool',
    aggs: ['sum'],
    charts: ['line', 'bar', 'table'],
    chart: 'line',
    variants: [
      { metric: ['errors'], set: { by: ['tool', 'error_kind'] } },
      { metric: ['request_failures', 'tool_rejects', 'retries'], set: { by: ['tool', 'method'] } },
    ],
    top: false,
    prev: true,
    unit: true,
    hide0: true,
    smallGroup: false,
    noPerson: true,
    fixedNotes: [],
  },
  'api-calls': {
    item: 'api',
    phase: 'P4',
    metrics: ['gateway_call_count', 'key_usage', 'wait'],
    metric: 'gateway_call_count',
    by: ['tool', 'api_key', 'model', 'none'],
    byDefault: 'tool',
    aggs: ['sum'],
    charts: ['stacked_bar', 'table'],
    chart: 'stacked_bar',
    // Call wait: typical/slow lines only, no comparison.
    variants: [{ metric: ['wait'], set: { by: ['none'], byDefault: 'none', aggs: ['p50'], agg: 'p50', charts: ['line', 'table'], chart: 'line' } }],
    top: true,
    prev: true,
    unit: true,
    hide0: true,
    smallGroup: false,
    noPerson: true,
    fixedNotes: ['via_gateway_separate'],
  },
  'api-reconcile': {
    item: 'api',
    phase: 'P4',
    metrics: ['cost_price_table'],
    metric: 'cost_price_table',
    by: null,
    byDefault: 'none',
    aggs: ['sum'],
    charts: ['table', 'bar'],
    chart: 'table',
    top: false,
    prev: false,
    unit: false,
    hide0: false,
    smallGroup: false,
    noPerson: true,
    fixedNotes: ['org_report_daily'],
  },
  'api-costs': {
    item: 'api',
    phase: 'P4',
    metrics: ['cost_cli_estimate', 'cost_unofficial', 'cost_price_table', 'cost_org_billed'],
    metric: 'cost_cli_estimate',
    by: null,
    byDefault: 'cost_kind',
    fixedBy: 'cost_kind',
    aggs: ['sum'],
    charts: ['hbar', 'table'],
    chart: 'hbar',
    top: false,
    prev: false,
    unit: false,
    hide0: false,
    unoff: true,
    smallGroup: false,
    noPerson: true,
    fixedNotes: ['cost_kinds_not_summed', 'not_bill'],
  },
};

export const ITEM_CARDS: Record<StatsItem, CardId[]> = Object.fromEntries(
  STATS_ITEMS.map((item) => [item, CARD_IDS.filter((c) => CARD_SPECS[c].item === item)]),
) as Record<StatsItem, CardId[]>;

/**
 * The effective choices of a card for a metric/comparison pair: metric variants first, then the comparison is settled
 * (an unavailable one falls back to the default), then comparison variants. Web option panels and the server's
 * option check both use this, so a combination the panel offers is never reset by the server.
 */
export function resolveCardSpec(cardId: CardId, choice: { metric?: string; by?: string } = {}): ResolvedCardSpec {
  const s = CARD_SPECS[cardId];
  const metric = (s.metrics as readonly string[]).includes(choice.metric ?? '') ? (choice.metric as CardMetric) : s.metric;
  let r: ResolvedCardSpec = {
    metrics: s.metrics,
    metric,
    by: s.by,
    byValue: s.byDefault,
    byDefault: s.byDefault,
    aggs: s.aggs,
    agg: s.aggs[0] ?? 'sum',
    charts: s.charts,
    chart: s.chart,
    tops: s.top ? TOP_OPTIONS : [],
    disps: s.disp ? DISP_OPTIONS : ['auto'],
  };
  const apply = (v: CardVariant): void => {
    const x = v.set;
    r = {
      ...r,
      ...(x.by !== undefined ? { by: x.by } : {}),
      ...(x.byDefault ? { byDefault: x.byDefault } : {}),
      ...(x.aggs ? { aggs: x.aggs, agg: x.agg ?? x.aggs[0] ?? r.agg } : {}),
      ...(x.agg ? { agg: x.agg } : {}),
      ...(x.charts ? { charts: x.charts, chart: x.chart ?? x.charts[0] ?? r.chart } : {}),
      ...(x.chart ? { chart: x.chart } : {}),
      ...(x.tops ? { tops: x.tops } : {}),
      ...(x.disps ? { disps: x.disps } : {}),
    };
  };
  const variants = s.variants ?? [];
  for (const v of variants) if (v.metric && !v.by && v.metric.includes(metric)) apply(v);
  const wanted = choice.by as ByKey | undefined;
  r.byValue = r.by && wanted && r.by.includes(wanted) ? wanted : r.byDefault;
  for (const v of variants) if (v.by && v.by.includes(r.byValue) && (!v.metric || v.metric.includes(metric))) apply(v);
  return r;
}

/**
 * Whether a request may use `value` for option `key` on `cardId`, given the chosen metric/comparison (`choice`);
 * returns the reason when not.
 */
export function optionProblem(cardId: CardId, key: OptionKey, value: string, choice: { metric?: string; by?: string } = {}): 'not_offered' | 'not_allowed' | null {
  const s = CARD_SPECS[cardId];
  const r = resolveCardSpec(cardId, choice);
  switch (key) {
    case 'metric':
      return (s.metrics as readonly string[]).includes(value) ? null : 'not_allowed';
    case 'by':
      if (!r.by) return value === r.byDefault ? null : 'not_offered';
      return (r.by as readonly string[]).includes(value) ? null : 'not_allowed';
    case 'agg':
      return (r.aggs as readonly string[]).includes(value) ? null : 'not_allowed';
    case 'chart':
      return (r.charts as readonly string[]).includes(value) ? null : 'not_allowed';
    case 'top':
      if (!r.tops.length) return 'not_offered';
      return (r.tops as readonly number[]).includes(Number(value)) ? null : 'not_allowed';
    case 'prev':
      return s.prev || value === '0' ? null : 'not_offered';
    case 'unit':
      return s.unit || value === 'auto' ? null : 'not_offered';
    case 'hide0':
      return s.hide0 || value === '0' ? null : 'not_offered';
    case 'split':
      return s.split || value === '0' ? null : 'not_offered';
    case 'unoff':
      return s.unoff || value === '0' ? null : 'not_offered';
    case 'disp':
      if (!s.disp) return value === 'auto' ? null : 'not_offered';
      return (r.disps as readonly string[]).includes(value) ? null : 'not_allowed';
    default:
      return null;
  }
}

/**
 * Mockup (stats.js) option values → contract values, so the web can translate links/ports written against the mockup.
 * The contract values are the only ones the API accepts.
 */
export const MOCKUP_OPTION_ALIASES = {
  by: { source: 'tool', authMode: 'auth_mode', teamAtEvent: 'team', teamNow: 'team_now', kind: 'token_kind', fileExt: 'ext', actionKind: 'action_group', actor: 'decision', errorKind: 'error_kind', apiKey: 'api_key', costKind: 'cost_kind' },
  agg: { perPerson: 'per_person' },
  chart: { stackedBar: 'stacked_bar', stackedArea: 'stacked_area', pct100: 'bar100' },
  unit: {},
  metric: {
    prompts: 'prompt_count',
    perConv: 'prompts_per_conversation',
    slashCommands: 'slash_command_count',
    questionsLen: 'prompt_length',
    tokens: 'tokens_total',
    'cost:cli_estimate': 'cost_cli_estimate',
    'cost:unofficial': 'cost_unofficial',
    'cost:gateway_price': 'cost_price_table',
    cacheReuseRatio: 'cache_reuse_ratio',
    filesByExt: 'files_by_ext',
    'files.read': 'files_opened',
    'files.edited': 'files_changed',
    filesEdited: 'files_changed',
    folders: 'files_by_folder',
    patches: 'patch_count',
    commits: 'commit_pr_count',
    linesByExt: 'lines_by_ext',
    activeMs: 'active_time',
    durationMs: 'conversation_open_time',
    latency: 'wait',
    firstToken: 'first_token',
    requests: 'request_count',
    tools: 'action_count',
    failRate: 'action_fail_rate',
    commands: 'command_count',
    decisionsBy: 'decisions',
    errorKinds: 'errors',
    // The mockup's actions-errors metric 'errors' (failed requests & refusals) is the contract's 'request_failures';
    // it cannot be listed here because 'errors' is itself a contract value (AI-call errors & tool failures).
    rejects: 'tool_rejects',
    gatewayCalls: 'gateway_call_count',
  },
} as const satisfies Record<string, Record<string, string>>;

// ───────────────────────── Saved views (plan 10.3.4) ─────────────────────────

export const SavedViewConditions = z.object({
  scope: ViewScope,
  /** Relative preset, or `from~to` only when a custom range was chosen. */
  period: PeriodParam,
  method: MethodFilter,
  team: TeamId.nullable(),
  tool: z.array(Tool).nullable(),
  model: z.array(z.string()).nullable(),
});
/** `options` keys are the screen URL keys `o.<cardId>.<option>`. */
const OptionsMap = z.record(z.string().regex(/^o\.[a-z-]+\.[a-z0-9]+$/), z.string().max(40));
export const SavedView = z.object({
  id: Id,
  name: z.string().min(1).max(40),
  item: StatsItem.nullable(),
  cards: z.array(CardId).nullable(),
  options: OptionsMap,
  conditions: SavedViewConditions,
  dictionaryVersion: z.string(),
  owner: PersonRef,
  mine: z.boolean(),
  /** private = only me · link = anyone with the link (re-judged with the opener's permissions). */
  sharedScope: SavedViewScope,
  createdAt: EpochMs,
  updatedAt: EpochMs,
});
export type SavedView = z.infer<typeof SavedView>;
export const SavedViewListResponse = z.object({ items: z.array(SavedView) });
export const SavedViewCreateRequest = z
  .object({
    name: z.string().trim().min(1).max(40),
    item: StatsItem.nullable().default(null),
    cards: z.array(CardId).max(20).nullable().default(null),
    options: OptionsMap.default({}),
    conditions: SavedViewConditions,
    sharedScope: SavedViewScope.default('private'),
  })
  .superRefine((b, ctx) => {
    if (!b.item === !b.cards) ctx.addIssue({ code: 'custom', message: 'exactly one of item or cards', path: ['item'] });
  });
/**
 * PATCH: adding a card to an item-based view turns it into a card list (`item: null` + `cards`), as the mockup does.
 * Exactly one of item/cards stays set after the merge (422 otherwise).
 */
export const SavedViewUpdateRequest = z.object({
  name: z.string().trim().min(1).max(40).optional(),
  item: StatsItem.nullable().optional(),
  cards: z.array(CardId).max(20).nullable().optional(),
  options: OptionsMap.optional(),
  conditions: SavedViewConditions.optional(),
  sharedScope: SavedViewScope.optional(),
});
/** Opening a shared link re-judges with the opener's permissions: hidden cards and reset options are reported. */
export const SavedViewOpenResponse = z.object({ view: SavedView, applied: AppliedFilter, hiddenCards: z.array(CardId), resetOptions: z.array(z.string()), scopeLowered: z.boolean() });
export const ViewParams = z.object({ viewId: Id });

/** POST /api/v1/stats/cards/:cardId/export — the card's table (aggregate; '<3' for small groups; dictionary version in header). */
export const StatsExportRequest = z
  .object({
    format: ExportFormat,
    query: StatsCardQuery,
    /** Real names in a by=person table need export.identity + a reason, like every identity export (plan 11.10). */
    identity: z.boolean().default(false),
    reasonCategory: ReasonCategory.optional(),
    reason: z.string().trim().max(200).optional(),
  })
  .superRefine((b, ctx) => {
    if (b.identity && (!b.reasonCategory || !b.reason || b.reason.length < 10)) ctx.addIssue({ code: 'custom', message: 'identity export needs a reason category and a reason of 10+ chars', path: ['reason'] });
  });

export const statsSchemas = {
  CardParams,
  StatsCardQuery,
  StatsCardResponse,
  StatsContextQuery,
  StatsContextResponse,
  UsageKpisResponse,
  SavedView,
  SavedViewListResponse,
  SavedViewCreateRequest,
  SavedViewUpdateRequest,
  SavedViewOpenResponse,
  ViewParams,
  StatsExportRequest,
} as const;
